import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { useToast } from '../../contexts/ToastContext';
import { getMyEnrollments, getMyCertificates } from '../../api/client';
import Card, { CardImage, CardContent } from '../../components/ui/Card';
import ProgressBar from '../../components/ui/ProgressBar';
import PageHeader from '../../components/layout/PageHeader';
import EmptyState from '../../components/layout/EmptyState';
import LoadingScreen from '../../components/layout/LoadingScreen';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import type { Enrollment } from '../../types/course';
import {
  BookOpen,
  ArrowRight,
  Clock,
  Award,
  ExternalLink,
  FileCheck,
} from 'lucide-react';

interface Certificate {
  id: number;
  certificate_number: string;
  course_name: string;
  completion_date: string;
  status: string;
}

export default function MyCoursesScreen(): React.JSX.Element {
  const toast = useToast();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [enrollRes, certRes] = await Promise.all([
          getMyEnrollments(),
          getMyCertificates().catch(() => ({ certificates: [] })),
        ]);
        setEnrollments(enrollRes.enrollments || []);
        setCertificates(certRes.certificates || []);
      } catch (error: any) {
        toast.show(error.message || 'Failed to load data', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingScreen message="Loading your courses..." />;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed': return <Badge variant="success">Completed</Badge>;
      case 'active': return <Badge variant="info">In Progress</Badge>;
      case 'pending': return <Badge variant="warning">Pending</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 animate-fade-in">
      <PageHeader
        title="My Courses"
        subtitle={`${enrollments.length} enrollment${enrollments.length !== 1 ? 's' : ''}`}
      />

      {/* Certificates Section */}
      {certificates.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <Award className="text-amber-500" size={20} />
            <h2 className="text-lg font-bold text-slate-900">My Certificates</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {certificates.map((cert) => (
              <Link
                key={cert.id}
                to={`/certificate/${cert.id}`}
                className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md hover:border-amber-200 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <FileCheck className="text-amber-600" size={20} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{cert.course_name}</p>
                  <p className="text-xs text-slate-500 font-mono">{cert.certificate_number}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {cert.completion_date ? new Date(cert.completion_date).toLocaleDateString() : ''}
                  </p>
                </div>
                <ExternalLink className="text-slate-300 shrink-0 ml-auto" size={16} />
              </Link>
            ))}
          </div>
        </motion.div>
      )}

      {enrollments.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No courses yet"
          description="You haven't enrolled in any courses. Start your learning journey today!"
          action={
            <Link to="/courses">
              <Button leftIcon={<ArrowRight className="w-4 h-4" />}>
                Explore Courses
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {enrollments.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Link to={`/courses/${item.course_slug}`} className="block">
                <Card className="flex flex-col sm:flex-row overflow-hidden">
                  <CardImage
                    src={item.course_image}
                    alt={item.course_title}
                    className="w-full sm:w-40 h-48 sm:h-auto flex-shrink-0"
                  />
                  <CardContent className="flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      {getStatusBadge(item.status)}
                      {item.status === 'completed' && certificates.some(c => c.course_name === item.course_title) && (
                        <Badge variant="purple" size="sm">
                          <Award className="w-3 h-3 mr-1" />
                          Certified
                        </Badge>
                      )}
                    </div>

                    <h3 className="text-base font-bold text-slate-900 mb-1 line-clamp-2">
                      {item.course_title}
                    </h3>

                    {item.duration && (
                      <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {item.duration}
                      </p>
                    )}

                    <div className="mt-auto space-y-3">
                      <ProgressBar
                        progress={item.progress_percent || 0}
                        color={item.status === 'completed' ? 'green' : 'blue'}
                        showLabel
                      />

                      <div className="flex items-center justify-between pt-2">
                        {item.started_at && (
                          <p className="text-xs text-slate-400">
                            Started {new Date(item.started_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
